import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import GoogleButton from './GoogleButton';
import EmailLogin from './EmailLogin';
import { GoogleUser } from '../services/googleAuth';
import { useAuth } from '../contexts/AuthContext';
import './LoginForm.css';

const LoginForm: React.FC = () => {
  const { login, logout, isLoggedIn, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 获取来源页面路径，如果没有则默认返回主页
  const from = (location.state as any)?.from?.pathname || '/';

  const handleGoogleLoginSuccess = async (googleUser: GoogleUser) => {
    console.log('User login successful:', googleUser);
    try {
      await login(googleUser);
      // 等待状态更新完成后再导航
      // 使用requestAnimationFrame确保DOM更新完成
      requestAnimationFrame(() => {
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);
      });
    } catch (error) {
      console.error('Login processing failed:', error);
      alert('Login failed, please try again.');
    }
  };

  const handleGoogleLoginError = (error: any) => {
    console.error('Google login failed:', error);
    alert('Google login failed, please try again later.');
  };

  const handleEmailLoginSuccess = async (userData: any) => {
    console.log('Email login successful:', userData);
    try {
      await login(userData);
      // 等待状态更新完成后再导航
      // 使用requestAnimationFrame确保DOM更新完成
      requestAnimationFrame(() => {
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);
      });
    } catch (error) {
      console.error('Login processing failed:', error);
      alert('Login failed, please try again.');
    }
  };

  const handleEmailLoginError = (error: any) => {
    console.error('Email login failed:', error);
    alert(`Login failed: ${error}`);
  };

  // 加载中显示加载状态
  if (isLoading) {
    return (
      <div className="login-form-section">
        <div className="login-form-container">
          <div className="login-form-content">
            <div className="login-header">
              <div className="welcome-section">
                <h1 className="welcome-text" style={{ textAlign: 'center' }}>
                  <span className="loading-dots">···</span>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 如果用户已登录，显示欢迎信息 (Display welcome message if user is logged in)
  if (isLoggedIn && user) {
    return (
      <div className="login-form-section">
        <div className="login-form-container">
          <div className="login-form-content">
            <div className="login-header">
              <div className="welcome-section">
                <h1 className="welcome-text">Welcome back,</h1>
                <h2 className="user-name">{user.name?.split(' ')[0] || user.email.split('@')[0]}!</h2>
              </div>
              <div className="user-info">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="user-avatar"
                  />
                ) : (
                  <div className="user-avatar avatar-placeholder">
                    {user.name?.charAt(0) || user.email.charAt(0)}
                  </div>
                )}
                <p className="user-email">{user.email}</p>
                <p className="user-credits">Credits: {user.credits}</p>
              </div>
            </div>
            <div className="logged-in-actions">
              <button 
                className="continue-button"
                onClick={() => navigate('/')}
              >
                Continue to Dashboard
              </button>
              <button 
                className="logout-button"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="login-form-section">
      <div className="login-form-container">
        <div className="login-form-content">
          
          {/* Header Section */}
          <div className="login-header">
            <div className="welcome-section">
              <h1 className="welcome-text">Welcome to</h1>
              <Logo />
            </div>
            <p className="subtitle">Continue to get free credits</p>
          </div>

          {/* Form Content */}
          <div className="form-content">
            
            {/* Google Login */}
            <div className="continue-with-section">
              <GoogleButton 
                onLoginSuccess={handleGoogleLoginSuccess}
                onLoginError={handleGoogleLoginError}
              />
            </div>

            {/* Divider */}
            <div className="divider">
              <div className="divider-line"></div>
              <span className="divider-text">OR</span>
              <div className="divider-line"></div>
            </div>

            {/* Email Login */}
            <EmailLogin 
              onLoginSuccess={handleEmailLoginSuccess}
              onLoginError={handleEmailLoginError}
            />

          </div>

          {/* Terms of Service */}
          <div className="tos-section">
            <p className="tos-text">
              By clicking "Continue with Google" or "Continue with Email", you agree to our{' '}
              <a 
                href="https://glistar.ai/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="tos-link"
              >
                Terms of Service
              </a>{' '}
              and acknowledge that you have read and understand our{' '}
              <a 
                href="https://glistar.ai/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="tos-link"
              >
                Privacy Policy
              </a>.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginForm;
